'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import React from 'react';

import { getFormContact } from '@/app/[locale]/[domain]/(default-layout)/(forms)/_components/form-contact-helper';
import {
    resolveFormPageText,
    resolveFormPageTitle,
    resolveFormImageUrl
} from '@/app/[locale]/[domain]/(default-layout)/(forms)/_components/form-image-helper';
import FormContactLinks from '@/app/[locale]/[domain]/(default-layout)/(forms)/_components/FormContactLinks';
import ReservationForm, {
    ReservationContentOption
} from '@/app/[locale]/[domain]/(default-layout)/(forms)/_components/ReservationForm';

import { useIsMounted } from '@cineamo/ui/src/hooks/useIsMounted';

import CinemaDescriptionMarkdown from '@cineamo/ui/src/markdown/CinemaDescriptionMarkdown';

import CenterLayout from '@cineamo/composables/src/CenterLayout';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

type ReservationPageClientProps = {
    contentOptions: ReservationContentOption[];
    initialContentId?: string;
    initialShowingId?: string;
    /** Fallback hero image (first cinema room picture), used when no image is configured. */
    hallImageUrl?: string;
};

const ReservationPageClient: React.FC<ReservationPageClientProps> = ({
    contentOptions,
    initialContentId,
    initialShowingId,
    hallImageUrl
}) => {
    const t = useTranslations();
    const isMounted = useIsMounted();
    const domainConfiguration = useDomainConfiguration();

    const { phone, phoneURI, email } = getFormContact(domainConfiguration, 'reservation');

    const heroImage = resolveFormImageUrl(domainConfiguration, 'reservation', hallImageUrl);
    const customDescription = resolveFormPageText(domainConfiguration, 'reservation');
    const customPageTitle = resolveFormPageTitle(domainConfiguration, 'reservation');

    return (
        <main className="min-h-screen">
            {/* Reservation Form Section */}
            <section className="py-24">
                <CenterLayout>
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-16 space-y-6">
                            <h1 className="text-headline-32 text-[var(--on-background-color)]">
                                {customPageTitle || t('reservation.page-title')}
                            </h1>
                            <div className="text-body-16 max-w-2xl mx-auto text-[rgba(var(--on-background-color-rgb),0.8)]">
                                {customDescription ? (
                                    <CinemaDescriptionMarkdown
                                        description={customDescription}
                                        allowInlineHtml
                                    />
                                ) : (
                                    t('reservation.hero.subtitle')
                                )}
                            </div>
                            {heroImage && isMounted && (
                                <div className="relative w-full max-w-2xl mx-auto aspect-[16/9] rounded-12 overflow-hidden">
                                    <Image
                                        src={heroImage}
                                        alt={t('reservation.page-title')}
                                        fill
                                        className="object-cover"
                                        sizes="(max-width: 768px) 100vw, 672px"
                                    />
                                </div>
                            )}
                        </div>

                        <ReservationForm
                            contentOptions={contentOptions}
                            initialContentId={initialContentId}
                            initialShowingId={initialShowingId}
                        />
                    </div>
                </CenterLayout>
            </section>

            {/* Contact Section */}
            <section className="mt-48 py-24 backdrop-blur-sm bg-[rgba(var(--on-background-color-rgb),0.05)]">
                <CenterLayout>
                    <div className="text-center space-y-8">
                        <h2 className="text-headline-32 text-[var(--on-background-color)]">
                            {t('reservation.contact.question')}
                        </h2>
                        <p className="text-body-16 max-w-2xl mx-auto text-[rgba(var(--on-background-color-rgb),0.8)]">
                            {t('reservation.contact.help-text')}
                        </p>
                        <FormContactLinks
                            namespace="reservation"
                            phone={phone}
                            phoneURI={phoneURI}
                            email={email}
                        />
                    </div>
                </CenterLayout>
            </section>
        </main>
    );
};

export default ReservationPageClient;
